import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import { extractApiErrorMessage } from '../../../utils/apiError'

export interface TimeOffAllocation {
  toReqHrsDtlId: number
  toReqDtlId: number
  hours: number
  toTypeCd: string
  toSubTypeCd: string | null
}

export interface OtherRequestEntry {
  toReqId: string
  status: string
  toReqDtlId: number
}

export interface OtherReqDetail {
  toReqDtlId: number
  toReqId: string
  partialRefId: string | null
  durationFlag: 'F' | 'P'
  day: string
  startTime: string | null
  endTime: string | null
  isOverlapped: boolean
  status: string
  timeOffAllocations: TimeOffAllocation[]
  otherRequests: OtherRequestEntry[]
}

/** API response is an object keyed by date string (YYYY-MM-DD), with array of details per date */
export type OtherReqDetailsResponse = Record<string, OtherReqDetail[]>

export interface OtherReqDetailsState {
  data: OtherReqDetailsResponse
  loading: boolean
  error: string | null
}

// Types for Create Time Off Request
export interface TimeOffAllocationDetail {
  hours: string
  toTypeCd: string
  toSubTypeCd: string | null
}

export interface TimeOffDetail {
  day: string
  durationFlag: 'F' | 'P'
  startTime: string | null
  endTime: string | null
  timeOffAllocations: TimeOffAllocationDetail[]
}

export interface CreateTimeOffRequestPayload {
  nuid: string
  startDate: string
  endDate: string
  isSingleDay: 'Y' | 'N'
  toReqId: string
  status: string
  comments: string
  appCode: string
  torRank?: number | null // Optional - required only for vacation bid (appCode: 'EMP_VB')
  timeOffDetails: TimeOffDetail[]
}

export interface CreateTimeOffRequestResponse {
  success: boolean
  timeOffRequestId: string
  toReqId?: string
  status?: string
  message?: string
}

export interface CreateNewRequestState {
  submitLoading: boolean
  submitError: string | null
  submitSuccess: boolean
  lastSubmittedRequest: CreateTimeOffRequestPayload | null
  deleteLoading: boolean
  deleteError: string | null
  deleteSuccess: boolean
  withdrawLoading: boolean
  withdrawError: string | null
  withdrawSuccess: boolean
}

const initialState: OtherReqDetailsState & CreateNewRequestState = {
  data: {},
  loading: false,
  error: null,
  submitLoading: false,
  submitError: null,
  submitSuccess: false,
  lastSubmittedRequest: null,
  deleteLoading: false,
  deleteError: null,
  deleteSuccess: false,
  withdrawLoading: false,
  withdrawError: null,
  withdrawSuccess: false,
}

export interface FetchOtherReqDetailsParams {
  month: number
  year: number
}

export const getOtherReqDetails = createAsyncThunk(
  'otherReqDetails/getOtherReqDetails',
  async (params: FetchOtherReqDetailsParams, { rejectWithValue, signal }) => {
    try {
      const url = endpoints.requester.getOtherReqDetails(
        params.month,
        params.year
      )
      const response = await api.get(url, { signal })
      return response.data as OtherReqDetailsResponse
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to fetch other request details')
      )
    }
  }
)

/**
 * Thunk to create a new time-off request
 * @param payload The time-off request data to submit
 * @returns The API response with the created request ID
 */
export const createTimeOffRequest = createAsyncThunk(
  'createNewRequest/createTimeOffRequest',
  async (payload: CreateTimeOffRequestPayload, { rejectWithValue, signal }) => {
    try {
      const url = endpoints.requester.createTimeOffRequest
      const response = await api.post(url, payload, { signal })
      return response.data as CreateTimeOffRequestResponse
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to create time-off request')
      )
    }
  }
)

/**
 * Thunk to withdraw a time off request by ID.
 * Kept in this slice per request; components can dispatch it as needed.
 */
export const withdrawTimeOffRequest = createAsyncThunk(
  'createNewRequest/withdrawTimeOffRequest',
  async (
    params: { requestId: string; empNuid: string },
    { rejectWithValue, signal }
  ) => {
    try {
      const url = endpoints.requester.withdrawTimeOffRequest(params.requestId)
      // API expects a PUT with a JSON body containing empNuid per OpenAPI
      await api.put(url, { empNuid: params.empNuid }, { signal })
      return { requestId: params.requestId }
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to withdraw time-off request')
      )
    }
  }
)

/**
 * Delete time-off request
 * @param requestId The ID of the request to delete
 * @param empNuid The employee NUID for the request body
 * @returns Success response
 */
export const deleteTimeOffRequest = createAsyncThunk(
  'createNewRequest/deleteTimeOffRequest',
  async (
    { requestId, empNuid }: { requestId: string; empNuid: string },
    { rejectWithValue, signal }
  ) => {
    try {
      const url = endpoints.requester.deleteTimeOffRequest(requestId)
      const response = await api.delete(url, {
        data: { empNuid },
        signal,
      })
      return response.data
    } catch (error: any) {
      console.error('[deleteTimeOffRequest thunk] Error:', error)
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to delete time-off request')
      )
    }
  }
)

const slice = createSlice({
  name: 'otherReqDetails',
  initialState,
  reducers: {
    clearOtherReqDetails: (state) => {
      state.data = {}
      state.error = null
    },
    clearSubmitState: (state) => {
      state.submitLoading = false
      state.submitError = null
      state.submitSuccess = false
      state.lastSubmittedRequest = null
    },
    clearWithdrawState: (state) => {
      state.withdrawLoading = false
      state.withdrawError = null
      state.withdrawSuccess = false
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getOtherReqDetails.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getOtherReqDetails.fulfilled, (state, action) => {
      state.loading = false
      state.data = action.payload
    })
    builder.addCase(getOtherReqDetails.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload as string
    })

    // Handle createTimeOffRequest thunk
    builder.addCase(createTimeOffRequest.pending, (state) => {
      state.submitLoading = true
      state.submitError = null
      state.submitSuccess = false
    })
    builder.addCase(createTimeOffRequest.fulfilled, (state) => {
      state.submitLoading = false
      state.submitSuccess = true
      state.submitError = null
      state.lastSubmittedRequest = null
    })
    builder.addCase(createTimeOffRequest.rejected, (state, action) => {
      state.submitLoading = false
      state.submitSuccess = false
      state.submitError = action.payload as string
    })

    // Withdraw handlers
    builder.addCase(withdrawTimeOffRequest.pending, (state) => {
      state.withdrawLoading = true
      state.withdrawError = null
      state.withdrawSuccess = false
    })
    builder.addCase(withdrawTimeOffRequest.fulfilled, (state) => {
      state.withdrawLoading = false
      state.withdrawSuccess = true
      state.withdrawError = null
    })
    builder.addCase(withdrawTimeOffRequest.rejected, (state, action) => {
      state.withdrawLoading = false
      state.withdrawSuccess = false
      state.withdrawError = action.payload as string
    })

    // Handle deleteTimeOffRequest thunk
    builder.addCase(deleteTimeOffRequest.pending, (state) => {
      state.deleteLoading = true
      state.deleteError = null
      state.deleteSuccess = false
    })
    builder.addCase(deleteTimeOffRequest.fulfilled, (state) => {
      state.deleteLoading = false
      state.deleteSuccess = true
      state.deleteError = null
    })
    builder.addCase(deleteTimeOffRequest.rejected, (state, action) => {
      state.deleteLoading = false
      state.deleteSuccess = false
      state.deleteError = action.payload as string
    })
  },
})

export const { clearOtherReqDetails, clearSubmitState } = slice.actions
export default slice.reducer
